import { IconButton } from '@creditornot/cb-button';
import { Cross } from '@creditornot/cb-icons';
import { cbSemanticCssVars, elevationCss } from '@creditornot/cb-ingredients';
import React, { useCallback, useEffect } from 'react';
import { useDropzone, DropEvent, FileRejection } from 'react-dropzone';
import styled from 'styled-components';

import LazyLoadedThumbnail from 'modules/menu/components/LazyLoadedThumbnail';
import EmptyThumbnail from 'modules/menu/items/components/EmptyThumbnail';
import { HeaderImageUploadThumbnail } from 'modules/menu/items/components/Item/HeaderImageUpload';

const thumbnailSize = '56px';

type Props = {
  className?: string;
  blurhash: string | null;
  src: string | null;
  alt: string;
  index: number;
  canDelete: boolean;
  isDragActive?: boolean;
  isUploading?: boolean;
  hasActiveDropzone?: boolean;
  onDrop?: (files: File[], index: number) => void;
  onDelete?: (index: number) => void;
  onDragStateChange?: (isDragging: boolean, index: number) => void;
};

const DeleteButton = styled(IconButton).attrs({
  variant: 'subdued',
  size: 'tiny',
})`
  ${elevationCss(2)}
  display: none;
  position: absolute;
  top: 0;
  right: 0;
  transform: translate(25%, -25%);
  z-index: 1;
`;

const Root = styled.div<{ hasSrc: boolean }>`
  position: relative;
  display: inline-flex;
  background-color: ${cbSemanticCssVars.surfaceMain};
  border-radius: 8px;
  width: ${thumbnailSize};
  height: ${thumbnailSize};

  &:hover ${DeleteButton} {
    display: ${({ hasSrc }) => (hasSrc ? 'block' : 'none')};
  }
`;

const DroppableThumbnail = ({
  className,
  blurhash,
  src,
  alt,
  index = 0,
  isDragActive = false,
  isUploading = false,
  hasActiveDropzone = true,
  canDelete,
  onDrop,
  onDelete,
  onDragStateChange,
}: Props) => {
  const handleFileDrop = useCallback(
    (files: File[], _: FileRejection[], event: DropEvent) => {
      event.stopPropagation();

      onDrop?.(files, index);
    },
    [onDrop, index],
  );

  const handleCrossClick = useCallback(() => {
    onDelete?.(index);
  }, [onDelete, index]);

  const {
    getRootProps,
    getInputProps,
    isDragActive: isDragging,
  } = useDropzone({
    accept: {
      'image/jpeg': [],
      'image/png': [],
    },
    multiple: true,
    onDrop: handleFileDrop,
    useFsAccessApi: false,
    noClick: true,
    noKeyboard: true,
  });

  useEffect(() => {
    onDragStateChange?.(isDragging, index);
  }, [isDragging, index, onDragStateChange]);

  return (
    <Root className={className} {...getRootProps()} hasSrc={Boolean(src)}>
      <input {...getInputProps()} />
      {isDragActive || isUploading ? (
        <HeaderImageUploadThumbnail uploading={isUploading} disabled={!hasActiveDropzone} />
      ) : src ? (
        <LazyLoadedThumbnail alt={alt} blurhash={blurhash} src={src} />
      ) : (
        <EmptyThumbnail />
      )}
      {canDelete && (
        <DeleteButton onClick={handleCrossClick}>
          <Cross />
        </DeleteButton>
      )}
    </Root>
  );
};

export default DroppableThumbnail;
